import { Button, cn, Loader } from "@prelude-ai/design-system"
import { useSearch } from "@tanstack/react-router"
import { Download, X } from "lucide-react"
import { lazy, memo, Suspense, useDeferredValue, useMemo, useState } from "react"
import type { StepInfo } from "../types"

const FlowStateViewer = lazy(() => import("./FlowStateViewer"))
const ExecutionTraceTree = lazy(() => import("./ExecutionTraceTree"))
const PromptsPanel = lazy(() => import("./PromptsPanel"))

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

type TabId = "trace" | "state" | "prompts" | "starting-state"

type DevToolsPanelProps = {
  stepHistory: StepInfo[]
  stepPath: string[]
  flowState: Record<string, unknown>
  transcriptDownloadUrl?: string
  transcriptDownloadFilename?: string
  onCloseDevTools?: () => void
  initialTab?: TabId
  startingFlowStateEditor?: React.ReactNode
}

const EstimatedTranscriptSize = memo(
  ({ stepHistory, flowState }: { stepHistory: string[]; flowState: Record<string, unknown> }) => {
    const estimatedPayloadSize = useMemo(() => {
      const encoder = new TextEncoder()
      const transcriptBytes = encoder.encode(JSON.stringify(stepHistory)).length
      const flowStateBytes = encoder.encode(JSON.stringify(flowState)).length
      return formatBytes(transcriptBytes + flowStateBytes)
    }, [stepHistory, flowState])

    return <span className="text-[10px] text-muted-foreground">~{estimatedPayloadSize}</span>
  }
)
export default function DevToolsPanel({
  stepHistory,
  stepPath,
  flowState,
  transcriptDownloadUrl,
  transcriptDownloadFilename,
  onCloseDevTools,
  initialTab = "trace",
  startingFlowStateEditor
}: DevToolsPanelProps) {
  const { agent } = useSearch({ from: "__root__" })
  const showPrompts = import.meta.env.DEV && agent
  const [devToolsTab, setDevToolsTab] = useState<TabId>(initialTab)

  const deferredStepHistory = useDeferredValue(stepPath, [])
  const deferredFlowState = useDeferredValue(flowState, {})

  return (
    <div className="h-full flex flex-col">
      <div className="p-2 bg-muted/30 flex items-center justify-between">
        <div className="flex items-center gap-1">
          {startingFlowStateEditor && (
            <Button
              variant={devToolsTab === "starting-state" ? "secondary" : "ghost"}
              size="sm"
              onClick={() => setDevToolsTab("starting-state")}
              className={cn("text-xs", devToolsTab !== "starting-state")}
            >
              Starting State
            </Button>
          )}
          <Button
            variant={devToolsTab === "state" ? "secondary" : "ghost"}
            size="sm"
            onClick={() => setDevToolsTab("state")}
            className={cn("text-xs", devToolsTab !== "state")}
          >
            State
            {Object.keys(flowState).length > 0 && (
              <span className="text-[10px] opacity-70">({Object.keys(flowState).length})</span>
            )}
          </Button>
          <Button
            variant={devToolsTab === "trace" ? "secondary" : "ghost"}
            size="sm"
            onClick={() => setDevToolsTab("trace")}
            className={cn("text-xs", devToolsTab !== "trace")}
          >
            Trace
            {stepHistory.length > 0 && (
              <span className="text-[10px] opacity-70">
                ({stepHistory.reduce((acc, s) => acc + s.turns.length, 0)})
              </span>
            )}
          </Button>
          {showPrompts && (
            <Button
              variant={devToolsTab === "prompts" ? "secondary" : "ghost"}
              size="sm"
              onClick={() => setDevToolsTab("prompts")}
              className={cn("text-xs")}
            >
              Prompts
            </Button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Suspense fallback={<Loader />}>
            <EstimatedTranscriptSize stepHistory={deferredStepHistory} flowState={deferredFlowState} />
          </Suspense>
          {transcriptDownloadUrl && transcriptDownloadFilename && (
            <Button variant="outline" size="sm" asChild className="h-7 text-xs gap-1.5">
              <a href={transcriptDownloadUrl} download={transcriptDownloadFilename}>
                <Download className="h-3.5 w-3.5" />
                Download Transcript
              </a>
            </Button>
          )}
          {onCloseDevTools && (
            <Button variant="ghost" size="sm" onClick={onCloseDevTools} className="h-6 w-6 p-0">
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
      <div
        className={cn(
          "flex-1 flex",
          startingFlowStateEditor && devToolsTab === "starting-state" ? "overflow-visible" : "overflow-auto",
          devToolsTab !== "prompts" && "p-4"
        )}
      >
        <Suspense
          fallback={
            <div className="justify-self-center flex self-center flex-1 justify-center">
              <Loader />
            </div>
          }
        >
          {devToolsTab === "starting-state" && startingFlowStateEditor ? (
            <div className="flex flex-1 flex-col -m-4">{startingFlowStateEditor}</div>
          ) : devToolsTab === "trace" ? (
            <ExecutionTraceTree steps={stepHistory} stepPath={stepPath} />
          ) : devToolsTab === "state" ? (
            <FlowStateViewer flowState={flowState} fullHeight />
          ) : showPrompts ? (
            <PromptsPanel agentName={agent} />
          ) : null}
        </Suspense>
      </div>
    </div>
  )
}
